<script setup lang="ts">
import { Star, MessageSquare, MessageSquareWarning, Frown } from 'lucide-vue-next'
import type { FunctionalComponent } from 'vue'
import type { LucideProps } from 'lucide-vue-next'
import type { ReviewStatsSummary } from '@/types/reviewPlatforms'

const props = defineProps<{
  stats: ReviewStatsSummary
  isLoading?: boolean
}>()

const { t } = useI18n()

type StatCard = {
  key: keyof ReviewStatsSummary
  label: string
  icon: FunctionalComponent<LucideProps>
  iconClass: string
  formatted: string
  suffix?: string
}

const cards = computed<StatCard[]>(() => [
  {
    key: 'total',
    label: t('pages.reviewPlatforms.reviews.stats.total'),
    icon: MessageSquare,
    iconClass: 'text-muted-foreground',
    formatted: String(props.stats.total)
  },
  {
    key: 'averageRating',
    label: t('pages.reviewPlatforms.reviews.stats.average'),
    icon: Star,
    iconClass: 'fill-amber-400 text-amber-400',
    formatted: props.stats.averageRating.toFixed(1),
    suffix: '/ 5'
  },
  {
    key: 'unrepliedCount',
    label: t('pages.reviewPlatforms.reviews.stats.unreplied'),
    icon: MessageSquareWarning,
    iconClass: 'text-amber-500',
    formatted: String(props.stats.unrepliedCount)
  },
  {
    key: 'negativeCount',
    label: t('pages.reviewPlatforms.reviews.stats.negative'),
    icon: Frown,
    iconClass: 'text-red-500',
    formatted: String(props.stats.negativeCount)
  }
])
</script>

<template>
  <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <div v-for="card in cards" :key="card.key" class="rounded-xl border border-border bg-card p-4">
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-muted-foreground">{{ card.label }}</span>
        <component :is="card.icon" class="size-4" :class="card.iconClass" />
      </div>
      <p class="mt-2 text-2xl font-semibold text-foreground">
        <Skeleton v-if="isLoading" class="h-7 w-12" />
        <template v-else>
          {{ card.formatted }}
          <span v-if="card.suffix" class="text-sm font-normal text-muted-foreground">{{ card.suffix }}</span>
        </template>
      </p>
    </div>
  </div>
</template>
